import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  UserPlusIcon, 
  PlusIcon, 
  TrashIcon,
  EnvelopeIcon 
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const InviteTeamModal = ({ isOpen, onClose, onInvite, organizationName }) => {
  const [invites, setInvites] = useState([
    { email: '', role: 'member', name: '' }
  ]);
  const [isInviting, setIsInviting] = useState(false);

  const roles = [
    { id: 'admin', label: 'Admin', description: 'Full access to organization' },
    { id: 'member', label: 'Member', description: 'Can create and edit own content' },
    { id: 'viewer', label: 'Viewer', description: 'Read-only access' }
  ];

  const addInvite = () => {
    setInvites([...invites, { email: '', role: 'member', name: '' }]);
  };

  const removeInvite = (index) => {
    setInvites(invites.filter((_, i) => i !== index));
  };

  const updateInvite = (index, field, value) => {
    const updated = [...invites];
    updated[index][field] = value;
    setInvites(updated);
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all emails
    const validInvites = invites.filter(invite => invite.email);
    
    if (validInvites.length === 0) {
      toast.error('Please add at least one email address');
      return;
    }

    for (const invite of validInvites) {
      if (!validateEmail(invite.email)) {
        toast.error(`Invalid email: ${invite.email}`);
        return;
      }
    }

    setIsInviting(true);
    try {
      await onInvite(validInvites);
      toast.success(`Sent ${validInvites.length} invitation${validInvites.length > 1 ? 's' : ''}`);
      setInvites([{ email: '', role: 'member', name: '' }]);
      onClose();
    } catch (error) {
      console.error('Error sending invitations:', error);
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white dark:bg-[#111111] rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-[#2A2A2A]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-brand to-[#FF9D5C]">
                      <UserPlusIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Invite Team Members
                      </h2>
                      <p className="text-xs text-gray-500 dark:text-[#666666]">
                        to {organizationName}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-[#666666]" />
                  </button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-3 mb-4">
                  {invites.map((invite, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex gap-3"
                    >
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <input
                          type="email"
                          value={invite.email}
                          onChange={(e) => updateInvite(index, 'email', e.target.value)}
                          placeholder="email@example.com"
                          className="px-4 py-2 bg-gray-50 dark:bg-[#0F0F0F] border border-gray-200 dark:border-[#2A2A2A] rounded-xl focus:ring-2 focus:ring-brand focus:border-brand outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-[#666666]"
                          required
                        />
                        <input
                          type="text"
                          value={invite.name}
                          onChange={(e) => updateInvite(index, 'name', e.target.value)}
                          placeholder="Name (optional)"
                          className="px-4 py-2 bg-gray-50 dark:bg-[#0F0F0F] border border-gray-200 dark:border-[#2A2A2A] rounded-xl focus:ring-2 focus:ring-brand focus:border-brand outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-[#666666]"
                        />
                        <select
                          value={invite.role}
                          onChange={(e) => updateInvite(index, 'role', e.target.value)}
                          className="px-4 py-2 bg-gray-50 dark:bg-[#0F0F0F] border border-gray-200 dark:border-[#2A2A2A] rounded-xl focus:ring-2 focus:ring-brand focus:border-brand outline-none text-gray-900 dark:text-white"
                        >
                          {roles.map(role => (
                            <option key={role.id} value={role.id}>
                              {role.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {invites.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeInvite(index)}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors"
                        >
                          <TrashIcon className="w-5 h-5 text-gray-400 hover:text-red-500 transition-colors" />
                        </button>
                      )}
                    </motion.div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addInvite}
                  className="mb-6 flex items-center gap-2 px-3 py-2 text-sm font-medium text-brand hover:bg-brand/10 rounded-lg transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add another
                </button>

                {/* Role Descriptions */}
                <div className="mb-6 p-4 bg-gray-50 dark:bg-[#0F0F0F] rounded-xl">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    Role Permissions
                  </h3>
                  <div className="space-y-2">
                    {roles.map(role => (
                      <div key={role.id} className="flex items-start gap-3">
                        <span className="text-xs font-medium text-gray-700 dark:text-[#CCCCCC] min-w-[60px]">
                          {role.label}:
                        </span>
                        <span className="text-xs text-gray-500 dark:text-[#666666]">
                          {role.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Email Preview */}
                <div className="mb-6 p-4 border border-gray-200 dark:border-[#2A2A2A] rounded-xl">
                  <div className="flex items-start gap-3">
                    <EnvelopeIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        Invitation Email Preview
                      </p>
                      <p className="text-xs text-gray-500 dark:text-[#666666]">
                        Your team members will receive an email invitation to join <span className="font-medium">{organizationName}</span> on Klorah. 
                        They'll be able to create an account or sign in with their existing account to accept the invitation.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 border border-gray-200 dark:border-[#2A2A2A] text-gray-700 dark:text-[#CCCCCC] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isInviting}
                    className="flex-1 px-4 py-2 bg-brand hover:bg-brand-hover text-black rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isInviting ? 'Sending...' : `Send Invitation${invites.filter(i => i.email).length > 1 ? 's' : ''}`}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default InviteTeamModal;